import { useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Upload, Plus } from "lucide-react";
import ScopeSection from "./ScopeSection";

/**
 * One scope's deliverables, opened from its package card. Everything that used
 * to live in a section of a very long page is here instead, so the project page
 * stays one screen and the reader chooses what to look at.
 *
 * `lightboxOpen` matters more than it looks: the image viewer renders outside
 * this dialog, so without it Esc and a click on the viewer would close the
 * dialog underneath — and the reader would lose the scope they were in.
 */
export default function ScopeDialog({
  scope,
  group,
  inPackage,
  organizing,
  note,
  lightboxOpen,
  onClose,
  onNoteSave,
  onUpload,
  onAddLink,
  onEditItem,
  onDeleteItem,
  onSetCover,
  onReorder,
  onOpenImage,
  heroImageId,
  uploading,
  mediaUrl,
}) {
  const total = group?.all?.length || 0;
  const keepOpen = (e) => { if (lightboxOpen) e.preventDefault(); };

  return (
    <Dialog open={Boolean(scope)} onOpenChange={(open) => { if (!open && !lightboxOpen) onClose(); }}>
      <DialogContent
        className="max-w-5xl w-[calc(100vw-2rem)] max-h-[88vh] p-0 gap-0 flex flex-col overflow-hidden"
        onEscapeKeyDown={keepOpen}
        onPointerDownOutside={keepOpen}
        onInteractOutside={keepOpen}
        data-testid={`scope-dialog-${scope.key}`}
      >
        <header className="shrink-0 flex items-start justify-between gap-4 px-6 sm:px-7 py-5 border-b border-border pr-12">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <scope.Icon className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <DialogTitle className="text-xl sm:text-2xl truncate">{scope.label}</DialogTitle>
              {total > 0 && <span className="text-xs text-muted-foreground tabular shrink-0">{total}</span>}
              {/* Only worth saying while someone can act on it — a rep reading
                  the page doesn't care how the package was tagged. */}
              {organizing && !inPackage && total > 0 && (
                <span className="text-[10px] text-muted-foreground/70 italic shrink-0">not marked as sold</span>
              )}
            </div>
            <DialogDescription className="mt-1">{scope.blurb}</DialogDescription>
          </div>

          {organizing && (
            <ScopeActions
              scope={scope}
              uploading={uploading}
              onUpload={onUpload}
              onAddLink={onAddLink}
            />
          )}
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 sm:px-7 py-6">
          <ScopeSection
            scope={scope}
            group={group}
            inPackage={inPackage}
            organizing={organizing}
            note={note}
            onNoteSave={onNoteSave}
            onUpload={onUpload}
            onEditItem={onEditItem}
            onDeleteItem={onDeleteItem}
            onSetCover={onSetCover}
            onReorder={onReorder}
            onOpenImage={onOpenImage}
            heroImageId={heroImageId}
            mediaUrl={mediaUrl}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Upload and Add link — the two ways anything gets into a scope. They sit in
 *  the header so they stay put however far the contents scroll. */
function ScopeActions({ scope, uploading, onUpload, onAddLink }) {
  const fileInputRef = useRef(null);
  const acceptTypes = [
    scope.acceptsImages && "image/*",
    scope.acceptsDocuments && "application/pdf",
  ].filter(Boolean).join(",");

  return (
    <div className="flex items-center gap-2 shrink-0">
      {acceptTypes && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            multiple
            className="hidden"
            onChange={(e) => { onUpload(scope.key, e.target.files); e.target.value = ""; }}
          />
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-8"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid={`upload-${scope.key}`}
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </>
      )}
      <Button
        variant="outline"
        size="sm"
        className="rounded-full h-8"
        onClick={() => onAddLink(scope)}
        data-testid={`add-link-${scope.key}`}
      >
        <Plus className="w-3.5 h-3.5 mr-1.5" />
        Add link
      </Button>
    </div>
  );
}
